import {useContext, useLayoutEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useLocation} from "react-router-dom"
import {formatPrice} from "helpers/formatNumber";
import {yupResolver} from "@hookform/resolvers/yup";
import {ChargeBackContext} from "context/ChargeBackContext";
import {formSchema, formSchemaMessage} from "components/CustomTextField/schema";
import {OTHER_REASON, STATUS_LIST_CHARGEBACK, StatusEnum} from "components/ChargeBackDispute/config";
import {
  StatusContainer,
  StatusContainerDescription,
  StatusMessage,
  StatusName,
  StatusTotal
} from "components/styles/styles";
import {
  useAcceptChargeBackMutation,
  useDisputeChargeBackMutation,
  useLazyGetChargeBackListByIdQuery,
  useLazyGetProviderConfigQuery
} from "api";
import { DEFAULT_REASON_LIST } from "config/config";

export const initialDataModel = {
  reason: '',
  note: '',
  description: ''
}


export const useChargeBack = () => {
  const location = useLocation()
  const searchParams = new URLSearchParams( location.search );
  const id = searchParams.get( 'id' )
  const [loading, setLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [openDisputeModal, setOpenDisputeModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [acceptChargeBack] = useAcceptChargeBackMutation();
  const [disputeChargeBack] = useDisputeChargeBackMutation();
  const [getProviderConfig] = useLazyGetProviderConfigQuery();
  const [getChargeBackListById] = useLazyGetChargeBackListByIdQuery();
  let checkFormSchema = selectedReason === OTHER_REASON.OTHER ? formSchemaMessage : formSchema;
  const { setProvidersConfigData, setCurrentBusiness, setOrderData, allOrderData } = useContext(ChargeBackContext);
  const formMethods = useForm({
    mode: "all",
    defaultValues: initialDataModel,
    resolver: yupResolver(checkFormSchema),
  });

  const handleOpen = () => setOpenDisputeModal(true);
  const handleClose = () => {
    setOpenDisputeModal(false);
    formMethods.reset(initialDataModel,{ keepDirty: false });
    setSelectedReason('');
  };
  const handlePrint = () => window.print();

  const handleAccept = () => setOpenConfirmModal(true);

  const handleCloseConfirmModal = () => setOpenConfirmModal(false);


  const getChargeBackListByIdQuery = (paramsId) => {
    setLoading(true);
    getChargeBackListById(paramsId).then((res) => {
      if (!res.error) {
        let currentResponse = res?.data?.data;
        localStorage.setItem('business', currentResponse?.orderData.business[0]._id);
        setOrderData(currentResponse);
        setCurrentBusiness(currentResponse?.orderData.business[0]);
        setLoading(false);
      }
      handleClose();
    })
    getProviderConfig().then((response) => {
      if (!response.error) {
        let currentProviders = response?.data?.data;
        setProvidersConfigData(currentProviders?.providers);
        setLoading(false);
      } else setLoading(false);
      handleClose();
    })
    setTimeout(() => {
      setLoading(false);
    },500)
  }
  const handleConfirmModal = () => {
    setLoadingButton(true);
    acceptChargeBack(allOrderData?._id).then((res) => {
      if (!res.error) {
        getChargeBackListByIdQuery(id);
        setLoadingButton(false);
        setOpenConfirmModal(false);
      } else setLoadingButton(false);
    })
  }

  useLayoutEffect(() => {
    if(id) {
      getChargeBackListByIdQuery(id);
    }
  },[id])


  const handleFormSubmit = (values) => {
    const findObject = DEFAULT_REASON_LIST.find((item) => item.value === values.reason);
    setLoadingButton(true);
    let obj = {
      id: allOrderData?._id,
      data: {
        reason: findObject.key,
        description: values.reason === OTHER_REASON.OTHER ? values.message : findObject.value
      },
    }
    disputeChargeBack(obj).then((res) => {
      setLoadingButton(false);
      if (!res?.error) {
        getChargeBackListByIdQuery(id);
      }
    })
  }

  const handleChange = (event) => {
    formMethods.reset({
      reason: event.target.value || '',
      note: '',
    })
    setSelectedReason(event.target.value);
  }

  const handleStatus = (currentStatus) => {
    const statusKey = currentStatus?.name.toUpperCase().replace(/-/g, '_');
    return STATUS_LIST_CHARGEBACK[statusKey]
  }

  const formatDate = (dateString) => {
    const currentBusiness =  dateString?.orderData && dateString?.orderData?.business[0];
    const timeZoneId = currentBusiness?.timezone?.timeZoneId || 'America/Los_Angeles';
    if (dateString?.lastDayToDispute) {
      const date = new Date(dateString?.lastDayToDispute);
      const options = { year: 'numeric', month: 'long', day: 'numeric',  timeZone: timeZoneId  };
      return new Intl.DateTimeFormat('en-US', options).format(date);
    } else {
      const date = new Date(Date.now());
      const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: timeZoneId };
      return new Intl.DateTimeFormat('en-US', options).format(date);
    }
  };

  function getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  }

  const formatDateByStatus = (dateString, type) => {
    const currentBusiness =  dateString?.orderData && dateString?.orderData?.business[0];
    const timeZoneId = currentBusiness?.timezone?.timeZoneId || 'America/Los_Angeles';
    const dateToUse = type === StatusEnum.CREATED ? dateString?.requestDate || dateString?.createdAt : dateString?.status?.updatedAt;
    const date = new Date(dateToUse);

    const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timeZoneId };
    const dateOptions = { weekday: 'short', month: 'short', day: 'numeric',  timeZone: timeZoneId };

    const timePart = new Intl.DateTimeFormat('en-US', timeOptions).format(date);
    const datePart = new Intl.DateTimeFormat('en-US', dateOptions).format(date);

    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);

    return `${timePart} ${datePart}${ordinalSuffix}` || '';
  };

  const handleStatusSwitch = (currentStatus, total, data, isModal = false ) => {
    switch (currentStatus?.type) {
      case StatusEnum.CREATED:
        return (
          <>
            {!isModal && (
              <StatusContainer
                sx={{ color: currentStatus.color, background: currentStatus.background  }}
              >
                <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
                <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.CREATED)}</StatusName>
              </StatusContainer>
            )}
            <StatusContainerDescription
              sx={{
                color: currentStatus.color,
                background: currentStatus.background,
                marginTop: isModal ? 0 : 2,
             }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                The customer reported one or more missing items resulting in an error charge of <StatusTotal>{formatPrice(total)}</StatusTotal>.
                If you believe this was an error, you can dispute the error charge by <span>{formatDate(data)}</span>.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.DISPUTED:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.DISPUTED)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                The customer has raised concerns regarding their order, leading to an error charge of <StatusTotal>{formatPrice(total)}</StatusTotal>. We are currently investigating the issue.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.DISPUTE_APPROVED:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.DISPUTE_APPROVED)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                Your dispute of <StatusTotal>{formatPrice(data?.refundTotal)}</StatusTotal> was approved and you'll receive a refund in your next payout cycle.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.HOLD:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.HOLD)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                We've temporarily placed a hold on your account due to an ongoing investigation into reported issues with an order.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.EXPIRED:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.EXPIRED)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                The customer reported an issue with their order resulting in an error charge of <StatusTotal>{formatPrice(total)}</StatusTotal>.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.DENIED:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.DENIED)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                Here will be text.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.DECLINED:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.DECLINED)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                Here will be text.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.REJECTED:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.REJECTED)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                Your dispute for <StatusTotal>{formatPrice(total)}</StatusTotal> has been reviewed and is not eligible for a refund.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.REFUNDED:

        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.REFUNDED)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                You disputed the error charges in this order and received a refund of <StatusTotal>{formatPrice(data?.refundTotal)}</StatusTotal>.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.ACCEPTED:
        return (
            <>
              <StatusContainer
                  sx={{ color: currentStatus.color, background: currentStatus.background  }}
              >
                <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
                <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.ACCEPTED)}</StatusName>
              </StatusContainer>
              <StatusContainerDescription
                  sx={{ color: currentStatus.color, background: currentStatus.background  }}
              >
                <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
                <StatusMessage>
                  Accepted a chargeback of <StatusTotal>{formatPrice(total)}</StatusTotal>.
                </StatusMessage>
              </StatusContainerDescription>
            </>
        )
      case StatusEnum.SOLVED_BY_PROVIDER:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.SOLVED_BY_PROVIDER)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                Wrong order reported. Customer received a refund at no charge to you.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.UNKNOWN:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.UNKNOWN)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                The customer reported an issue with their order resulting in an error charge of <StatusTotal>{formatPrice(total)}</StatusTotal>.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      case StatusEnum.DISPUTE_NOT_ALLOWED:
        return (
          <>
            <StatusContainer
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusName>{currentStatus?.name} - {formatDateByStatus(data, StatusEnum.DISPUTE_NOT_ALLOWED)}</StatusName>
            </StatusContainer>
            <StatusContainerDescription
              sx={{ color: currentStatus.color, background: currentStatus.background  }}
            >
              <img src={currentStatus?.icon} alt={currentStatus.name || ''}/>
              <StatusMessage>
                The customer reported an issue with their order resulting in an error charge of <StatusTotal>{formatPrice(total)}</StatusTotal>.
              </StatusMessage>
            </StatusContainerDescription>
          </>
        )
      default: return <></>
    }
  }


  return {
    openDisputeModal,
    handleOpen,
    handleClose,
    handlePrint,
    setSelectedReason,
    selectedReason,
    handleChange,
    handleFormSubmit,
    formMethods,
    loading,
    handleStatus,
    handleStatusSwitch,
    handleAccept,
    handleCloseConfirmModal,
    handleConfirmModal,
    openConfirmModal,
    loadingButton,
  }
}